import React, {useState, useEffect, ChangeEvent} from "react";
import axios from "axios";
import {Token, createToken} from "./TokenUtils";
import Property from "./Property";
import Utils from "./Utils";
import {FaEye, FaEyeSlash} from "react-icons/fa";

interface ProfileState {
  message: string;
  messageColor: string;
  username: string;
  tokens: Token[];
  roles: string[];
}

const noCacheHeaders = {headers: {"Cache-Control": "no-cache"}};

const Profile: React.FC = () => {
  const [state, setState] = useState<ProfileState>({
    message: "",
    messageColor: "#f00",
    username: "",
    tokens: [],
    roles: [],
  });

  useEffect(() => {
    Utils.ensureFreshAccessToken().then((token) => {
      if (!token) {
        Utils.redirectToLogin();
        return;
      }
      fetchProfileInfo();
      fetchTokens();
    });
  }, []);

  const showMessage = (text: string, color: string) => {
    setState((prevState) => ({...prevState, message: text, messageColor: color}));
    setTimeout(() => {
      setState((prevState) => ({...prevState, message: ""}));
    }, Property.MESSAGE_TIMEOUT_MILLISECONDS);
  };

  const fetchTokens = () => {
    axios
      .get(Property.BASE_API_URL + "/api/tokens", noCacheHeaders)
      .then((response) => {
        const tokensRaw = response.data
          .map((token: any) => createToken(token))
          .filter((token: Token | null) => token !== null);
        setState((prevState) => ({...prevState, tokens: tokensRaw}));
      })
      .catch(() => {
        setState((prevState) => ({...prevState, tokens: []}));
      });
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setState((prevState) => ({...prevState, username: event.target.value}));
  };

  const fetchProfileInfo = () => {
    const url = Property.BASE_API_URL + "/api/users/me";

    axios
      .get(url, noCacheHeaders)
      .then((response) => {
        setState((prevState) => ({...prevState, username: response.data.username,
          roles: response.data.roles}));
      })
      .catch((error) => {
        if (!Utils.getAccessToken()) {
          Utils.redirectToLogin();
        }
        console.log(error);
      });
  };

  const updateUserInfo = () => {
    const url = Property.BASE_API_URL + "/api/users";

    const requestBody = {username: state.username};

    axios
      .put(url, requestBody)
      .then(() => {
        showMessage("Update succeeded", "#0f0");
      })
      .catch((error) => {
        showMessage(`${error.response.status}: ${error.response.data.message}`, "#f00");
      });
  };

  const toggleTokenVisibility = (index: number) => {
    setState((prevState) => ({
      ...prevState,
      tokens: prevState.tokens.map((token, idx) =>
        idx === index ? {...token, showToken: !token.showToken} : token
      )
    }));
  };

  const maskToken = (token: string) => {
    if (token.length <= 8) {
      return token; // If the token length is 8 or less, don't mask
    }
    const maskedLength = token.length - 8;
    return token.slice(0, 4) + '*'.repeat(maskedLength) + token.slice(-4);
  };

  const removeAllTokens = () => {
    axios
      .delete(Property.BASE_API_URL + "/api/tokens")
      .catch((error) => console.log(error))
      .finally(() => {
        Utils.logout();
        Utils.redirectToLogin();
      });
  };

  const {message, messageColor, username, tokens} = state;

  return (
    <div className="content-holder">
      <h2>Profile settings</h2>
      <span id="message" style={{color: messageColor}}>
        {message}
      </span>
      <p>Name</p>
      <input
        type="text"
        placeholder="name"
        id="name"
        value={username}
        onChange={handleChange}
      />
      <button onClick={updateUserInfo}>Save</button>
      <p>Roles</p>
      <input id="roles" value={state.roles?.join(", ")} disabled />

      <p>Active tokens</p>
      <table>
        <thead>
        <tr>
          <th style={{width: "320px"}}>JTI (identifier)</th>
          <th>Type</th>
          <th>Valid until</th>
          <th>Actions</th>
        </tr>
        </thead>
        <tbody>
        {tokens.map((token, index) => (
          <tr key={token.jti} style={{ backgroundColor: Utils.isTokenExpired(token)
              ? Property.COLOR_PINK
              : Utils.isSessionToken(token) ? Property.COLOR_GOLDEN : undefined }}>
            <td>
              {token.showToken ? token.jti : maskToken(token.jti)}
              <span onClick={() => toggleTokenVisibility(index)}
                    style={{cursor: "pointer", marginLeft: "10px", float: "right"}}>
                  {token.showToken ? <>{FaEyeSlash({})}</> : <>{FaEye({})}</>}
                </span>
            </td>
            <td>{token.type}</td>
            <td>{Utils.getTokenExpirationDate(token)?.toISOString()}</td>
            <td>
              <button
                onClick={() => {
                  Utils.deleteToken(token)
                    .then(() => {
                      setState(prevState => ({
                        ...prevState,
                        tokens: prevState.tokens.filter(t => t.jti !== token.jti)
                      }));

                      if (!Utils.getAccessToken()) {
                        Utils.redirectToLogin();
                      }
                    })
                    .catch(error => {
                      showMessage(`Failed to delete token: ${error.response?.data?.message || error.message}`, "#f00");
                    });
                }}
              >
                Delete
              </button>
            </td>
          </tr>
        ))}
        </tbody>
      </table>
      {tokens.length > 0 && (
        <button
          onClick={removeAllTokens}
          style={{ marginTop: '10px', backgroundColor: '#ff4444', color: 'white' }}
        >
          Remove All Tokens
        </button>
      )}
      <p>Latest events</p>
      <span>
        (yet it is empty, not filled and not supported, later here will be list of comments, profile updates etc. provided by <strong>Event Sourcing)</strong>
      </span>
      <table>
        <thead>
        <tr>
          <th>Sequence</th>
          <th>Operation</th>
          <th>Request Body</th>
          <th>Result</th>
        </tr>
        </thead>
        <tbody>
        <tr>
          <td>001</td>
          <td>UPDATE</td>
          <td>{"{some json}"}</td>
          <td>200, SUCCESS / PROCESSING / ...</td>
        </tr>
        </tbody>
      </table>
    </div>
  );
};

export default Profile;